import React, { useEffect, useState } from 'react';

// Helper: Color logic for scores
export const getScoreColor = (scoreVal) => {
  const score = parseFloat(scoreVal);
  if (isNaN(score)) return 'text-gray-600 bg-gray-50 border-gray-200'; // Fallback
  if (score >= 0.9) return 'text-green-600 bg-green-50 border-green-200';
  if (score >= 0.7) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
};

export const getProgressBarColor = (scoreVal) => {
  const score = parseFloat(scoreVal);
  if (isNaN(score)) return 'bg-gray-300'; // Fallback
  if (score >= 0.9) return 'bg-green-500';
  if (score >= 0.7) return 'bg-amber-500';
  return 'bg-red-500';
};

const ScoreCard = ({ data }) => {
  const { criteria, score, reasoning } = data;
  const numericScore = parseFloat(score);
  // If score is missing or invalid, default to 0 or hide
  const validScore = !isNaN(numericScore);
  const percentage = validScore ? Math.round(numericScore * 100) : 0;

  return (
    <div className="p-4 rounded-lg border border-gray-200 bg-white shadow-sm flex flex-col gap-3 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-center">
        <h4 className="font-semibold text-gray-800 text-sm uppercase tracking-wide truncate pr-2" title={criteria}>
          {criteria || "Unnamed Criteria"}
        </h4>
        {validScore && (
          <span className={`px-2 py-1 rounded text-xs font-bold border ${getScoreColor(numericScore)}`}>
            {percentage}%
          </span>
        )}
      </div>
      
      {/* Progress Bar */}
      {validScore && (
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ${getProgressBarColor(numericScore)}`} 
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      )}

      <p className="text-sm text-gray-600 leading-relaxed line-clamp-4 hover:line-clamp-none transition-all">
        {reasoning || "No details provided."}
      </p>
    </div>
  );
};

const DataAnalysisPopup = ({ isOpen, onClose, data }) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen || !data) return null;

  // --- Safe Data Accessors (Defensive Coding) ---
  
  // Final Score: Check if exists, otherwise null
  const hasFinalScore = data.hasOwnProperty("final_score") && data["final_score"] !== null;
  const finalScore = hasFinalScore ? parseFloat(data["final_score"]) : 0;

  // Arrays: Default to empty array if missing
  const suggestions = Array.isArray(data["suggestions"]) ? data["suggestions"] : [];
  const inconsistencies = Array.isArray(data["inconsistencies"]) ? data["inconsistencies"] : [];
  const grammarIssues = Array.isArray(data["grammatical_spelling_issues"]) ? data["grammatical_spelling_issues"] : [];
  const topicsAdded = Array.isArray(data["topics-added"]) ? data["topics-added"] : (Array.isArray(data["topics_added"]) ? data["topics_added"] : []);
  const topicsToAdd = Array.isArray(data["topics-to-add"]) ? data["topics-to-add"] : (Array.isArray(data["topics_to_add"]) ? data["topics_to_add"] : []);
  const scores = Array.isArray(data["scores"]) ? data["scores"] : [];

  // Boolean checks for rendering
  const hasSuggestions = suggestions.length > 0;
  const hasInconsistencies = inconsistencies.length > 0;
  const hasGrammar = grammarIssues.length > 0;
  const hasTopicsAdded = topicsAdded.length > 0;
  const hasTopicsToAdd = topicsToAdd.length > 0;
  const hasTopics = hasTopicsAdded || hasTopicsToAdd;
  const hasScores = scores.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 font-sans">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Dataset Quality Analysis</h2>
            <p className="text-sm text-gray-500">Automated Evaluation Report</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Top Section: Dynamic Layout */}
          {/* If suggestions exist, use grid. If not, center the score or hide top section if score is also missing. */}
          {(hasFinalScore || hasSuggestions) && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              
              {/* Final Score Card */}
              {hasFinalScore && (
                <div className={`${hasSuggestions ? 'lg:col-span-4' : 'lg:col-span-12'} bg-indigo-50 rounded-xl p-6 flex flex-col items-center justify-center text-center border border-indigo-100`}>
                  <h3 className="text-indigo-900 font-medium mb-4">Overall Quality Score</h3>
                  <div className="relative flex items-center justify-center mb-4">
                    <svg className="w-40 h-40 transform -rotate-90">
                      <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="10" fill="transparent" className="text-indigo-200" />
                      <circle 
                        cx="80" cy="80" r="70" 
                        stroke="currentColor" strokeWidth="10" 
                        fill="transparent" 
                        className={`transition-all duration-1000 ${finalScore >= 0.7 ? 'text-indigo-600' : 'text-amber-500'}`}
                        strokeDasharray={440}
                        strokeDashoffset={440 - (440 * finalScore)}
                      />
                    </svg>
                    <div className="absolute flex flex-col items-center">
                      <span className="text-5xl font-bold text-indigo-800">{Math.round(finalScore * 100)}</span>
                      <span className="text-sm text-indigo-600">/ 100</span>
                    </div>
                  </div>
                  
                  {/* Quick Stats (Only show if data exists) */}
                  {(hasInconsistencies || hasGrammar) && (
                    <div className="w-full flex justify-center gap-2 mt-2">
                      {hasInconsistencies && (
                        <div className="flex-1 p-2 rounded text-center border bg-red-50 border-red-200 text-red-700 max-w-[120px]">
                            <div className="text-lg font-bold">{inconsistencies.length}</div>
                            <div className="text-xs uppercase">Issues</div>
                        </div>
                      )}
                      {hasGrammar && (
                        <div className="flex-1 p-2 rounded text-center border bg-amber-50 border-amber-200 max-w-[120px]">
                            <div className="text-lg font-bold">{grammarIssues.length}</div>
                            <div className="text-xs uppercase">Typos</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Suggestions List (Conditional) */}
              {hasSuggestions && (
                <div className={`${hasFinalScore ? 'lg:col-span-8' : 'lg:col-span-12'} bg-white border border-gray-200 rounded-xl p-6 flex flex-col`}>
                  <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Recommended Actions
                  </h3>
                  <div className="flex-1 overflow-y-auto max-h-60 pr-2 custom-scrollbar">
                    <ul className="space-y-3">
                      {suggestions.map((suggestion, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                          <span className="mt-0.5 flex-shrink-0 w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </span>
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Warning Section: Inconsistencies (Only shows if there are issues) */}
          {/* ... (commented out in your code) */}

          {/* Scores Grid (Only shows if scores exist) */}
          {hasScores && (
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4 border-l-4 border-indigo-500 pl-3">
                Detailed Scoring
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {scores.map((item, index) => (
                  <ScoreCard key={index} data={item} />
                ))}
              </div>
            </div>
          )}

          {/* Topics Tags (Only shows if topics exist) */}
          {hasTopics && (
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
               {hasTopicsAdded && (
                 <div className="mb-6 last:mb-0">
                   <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
                      Topics Added
                   </h3>
                   <div className="flex flex-wrap gap-2">
                      {topicsAdded.map((topic, index) => (
                         <span 
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white border border-gray-300 text-gray-700 shadow-sm"
                         >
                          {topic}
                         </span>
                      ))}
                   </div>
                 </div>
               )}

               {hasTopicsToAdd && (
                 <div className="mb-0">
                   <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
                      Suggested Topics
                   </h3>
                   <div className="flex flex-wrap gap-2">
                      {topicsToAdd.map((topic, index) => (
                         <span 
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 border border-blue-200 text-blue-700 shadow-sm"
                         >
                          {topic}
                         </span>
                      ))}
                   </div>
                 </div>
               )}
            </div>
          )}

          {/* Empty State (If no data at all) */}
          {!hasFinalScore && !hasSuggestions && !hasInconsistencies && !hasScores && !hasTopics && (
             <div className="text-center py-20 text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>Evaluation is not available.</p>
             </div>
          )}

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
          <button 
            onClick={onClose}
            className="px-5 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>

      </div>
    </div>
  );
};

export const QualityReportInline = ({ data, onDetailClick, onAddTopic }) => {
  const [selectedTopics, setSelectedTopics] = useState([]);

  if (!data) return null;

  const hasFinalScore = data.hasOwnProperty("final_score") && data["final_score"] !== null;
  const finalScore = hasFinalScore ? parseFloat(data["final_score"]) : 0;
  const percentage = Math.round(finalScore * 100);

  const suggestions = Array.isArray(data["suggestions"]) ? data["suggestions"] : [];
  const scoreItems = Array.isArray(data["scores"]) ? data["scores"] : [];
  const topicsAdded = Array.isArray(data["topics-added"]) ? data["topics-added"] : (Array.isArray(data["topics_added"]) ? data["topics_added"] : []);
  
  const topicsToAddRaw = Array.isArray(data["topics-to-add"]) ? data["topics-to-add"] : (Array.isArray(data["topics_to_add"]) ? data["topics_to_add"] : []);
  const topicsToAdd = topicsToAddRaw.slice(0, 5); // Limit to 5 topics

  const handleToggleTopic = (topic) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter(t => t !== topic));
    } else {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  const handleAddSelected = () => {
    if (onAddTopic && selectedTopics.length > 0) {
      onAddTopic(selectedTopics.join(", "));
      setSelectedTopics([]);
    }
  };

  return (
    <div className="w-full bg-gradient-to-br from-blue-50/80 to-blue-50/80 backdrop-blur-sm border border-blue-100 rounded-xl p-4 mt-2 shadow-sm">
      {/* Header with Score */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
           <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
           </div>
           <div>
              <h4 className="text-sm font-bold text-blue-900 leading-none">Insight Report</h4>
              <p className="text-[10px] text-blue-600 mt-1 font-medium">Automatic Quality Audit</p>
           </div>
        </div>
        <div className="bg-white/80 px-3 py-1.5 rounded-lg border border-blue-100 text-center shadow-sm">
           <div className={`text-xl font-black leading-none ${finalScore >= 0.7 ? 'text-blue-800' : 'text-amber-600'}`}>
             {percentage}%
           </div>
           <div className="text-[8px] uppercase tracking-tighter text-blue-400 font-bold mt-0.5">Quality</div>
        </div>
      </div>

      {/* Topics Summary Section */}
      <div className="grid grid-cols-1 gap-4 mb-4">
        {/* Topics Added */}
        {topicsAdded.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
              Topics Covered
            </p>
            <div className="flex flex-wrap gap-1.5">
              {topicsAdded.map((topic, idx) => (
                <span key={idx} className="px-2 py-1 text-[10px] font-medium bg-white text-gray-700 rounded-md border border-gray-200 shadow-sm">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Suggested Topics (with Add Button) */}
        {topicsToAdd.length > 0 && (
          <div>
            <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
              Opportunities to Expand
            </p>
            <div className="flex flex-wrap gap-2">
              {topicsToAdd.map((topic, idx) => {
                const isSelected = selectedTopics.includes(topic);
                return (
                  <div key={idx} 
                       onClick={() => handleToggleTopic(topic)}
                       className={`group cursor-pointer flex items-center bg-white border ${isSelected ? 'border-blue-400 ring-1 ring-blue-400 bg-blue-50' : 'border-blue-100'} rounded-lg overflow-hidden shadow-sm hover:border-blue-300 transition-all`}>
                    <span className={`px-2 py-1 text-[10px] font-medium transition-colors ${isSelected ? 'text-blue-800' : 'text-blue-900'}`}>
                      {topic}
                    </span>
                    <div 
                      className={`px-2 py-1 border-l ${isSelected ? 'border-blue-400 text-blue-800 bg-blue-200' : 'border-blue-100 text-blue-600 bg-blue-50 group-hover:bg-blue-600 group-hover:text-white'} transition-colors flex items-center gap-1`}
                    >
                      <span className="text-[11px] font-bold leading-none">{isSelected ? '✓' : '+'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Generate Selected Button */}
            {selectedTopics.length > 0 && (
              <button 
                onClick={handleAddSelected}
                className="mt-3 w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[10px] font-bold shadow-sm transition-all flex items-center justify-center gap-2 animate-in fade-in zoom-in duration-200"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add {selectedTopics.length} Selected Topic{selectedTopics.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="h-px bg-blue-100 mb-4"></div>

      {/* Recommendations & Action Buttons */}
      <div className="flex flex-col gap-3">
        {suggestions.length > 0 && (
          <div className="bg-blue-600/5 rounded-lg p-2.5 border border-blue-100/50">
             <p className="text-[10px] font-bold text-blue-800 mb-1 flex items-center gap-1.5">
               <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                 <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1a1 1 0 112 0v1a1 1 0 11-2 0zM13.464 15.05l.707.707a1 1 0 01-1.414 1.414l-.707-.707a1 1 0 011.414-1.414zM14.95 14.95l.707.707a1 1 0 01-1.414 1.414l-.707-.707a1 1 0 011.414-1.414z" />
               </svg>
               AI Suggestion
             </p>
             <p className="text-[10px] text-gray-700 leading-relaxed italic font-medium">
               "{suggestions[0]}"
             </p>
          </div>
        )}

        <button 
          onClick={onDetailClick}
          className="w-full py-2 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 group"
        >
          <span>View Full Technical Report</span>
          <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default DataAnalysisPopup;